import React from 'react';

type LegacyVariant = 'primary' | 'secondary' | 'tertiary' | 'brand' | 'ghost' | 'danger' | 'glass';
type ButtonAppearance = 'solid' | 'bordered' | 'text' | 'icon';
type ButtonColor = 'primary' | 'secondary' | 'white';
type ButtonVariant = LegacyVariant | ButtonAppearance;
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon-sm' | 'icon-md';

export type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> & {
  variant?: ButtonVariant;
  color?: ButtonColor;
  appearance?: ButtonAppearance;
  size?: ButtonSize;
  asChild?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  /**
   * Raw mode: render `children` directly (no inner span wrapper) and skip the
   * size/variant token classes, only the bare interaction primitives plus the
   * caller's `className` are applied. Use for buttons with multi-element flex
   * children (nav items, accordions, dropdown triggers, toggle cards) that the
   * default content wrapper would collapse. The caller owns the full styling.
   */
  raw?: boolean;
};

const sizeStyles: Record<ButtonSize, string> = {
  /** Compact inline action (e.g. table-row buttons, filter chips). */
  sm: 'h-8 px-3 text-[12px]',
  /** Default for most UI buttons. Comfortable but not chunky. */
  md: 'h-9 px-4 text-[13px]',
  /** Page-level primary CTAs. Still substantial without dominating. */
  lg: 'h-11 px-5 text-[14px]',
  /** Icon button sizes intentionally unchanged. */
  'icon-sm': 'p-1.5 text-[13px]',
  'icon-md': 'p-[6px] text-[14px]',
};

const join = (...parts: Array<string | undefined | false>) => parts.filter(Boolean).join(' ');

/* Interaction model, matches the raw `<button>` pattern used in
   DispensaryLocations (Pickup/Delivery/Route). Clean color-only state
   transitions over 200ms. Every variant fully specifies bg + text +
   border for ALL states: rest / hover / focus / active / disabled.
   Focus mirrors hover so keyboard users get the same visible feedback
   as mouse users (no separate focus ring needed). */
const base =
  'btn-stable-hover-text group relative inline-flex w-auto items-center justify-center rounded-md font-semibold select-none ' +
  'transition-colors duration-200 ease-out ' +
  'focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-60';

/* Raw mode base, only the universal interaction primitives. No layout, shape,
   size, font, colors, or transition: the caller's className supplies all of it. */
const rawBase = 'btn-stable-hover-text group select-none focus:outline-none';

/* Hover destination is uniform across the brand palette: `bg-blue-100` +
   `text-constant-white` + `border-blue-100`. Only the REST state differs
   per variant. Light variants invert to the filled navy on hover;
   already-filled variants slightly lighten so the state change is still
   visible. Active mirrors hover (no extra step), keeps the system simple. */
const HOVER_NAVY =
  'hover:bg-blue-100 hover:border-blue-100 hover:text-constant-white ' +
  'focus-visible:bg-blue-100 focus-visible:border-blue-100 focus-visible:text-constant-white ' +
  'active:bg-blue-100 active:border-blue-100 active:text-constant-white';

const HOVER_GREEN =
  'hover:bg-green-60 hover:border-green-60 hover:text-constant-white ' +
  'focus-visible:bg-green-60 focus-visible:border-green-60 focus-visible:text-constant-white ' +
  'active:bg-green-50 active:border-green-50 active:text-constant-white';

const HOVER_GREEN_LIGHTEN =
  'hover:bg-green-50 hover:border-green-50 hover:text-constant-white ' +
  'focus-visible:bg-green-50 focus-visible:border-green-50 focus-visible:text-constant-white ' +
  'active:bg-green-60 active:border-green-60 active:text-constant-white';

const HOVER_RED =
  'hover:bg-red-60 hover:border-red-60 hover:text-constant-white ' +
  'focus-visible:bg-red-60 focus-visible:border-red-60 focus-visible:text-constant-white ' +
  'active:bg-red-60 active:border-red-60 active:text-constant-white';

const DISABLED_NEUTRAL =
  'disabled:bg-neutral-5 disabled:text-neutral-15 disabled:border-neutral-10';

const legacyStyles: Record<LegacyVariant, string> = {
  /* Filled navy CTA. Rest is already at the max-dark navy, so hover
     LIGHTENS one step to blue-80 (otherwise the hover would be invisible
     against the identical rest color). Active returns to blue-100. */
  /* Rest is neutral-70 (#343B91). Hover DARKENS to blue-100 rather than
     lightening: the lighten path landed on blue-80, which is the same hex as
     neutral-70, so a filled primary button would have had no hover state at
     all. */
  primary:
    'bg-neutral-70 text-constant-white border border-neutral-70 ' +
    `${HOVER_NAVY} ` +
    'disabled:bg-neutral-10 disabled:border-neutral-10 disabled:text-neutral-15',
  /* White card with thin navy border. Hover INVERTS to filled navy. */
  secondary:
    'bg-constant-white text-blue-100 border border-blue-80 ' +
    `${HOVER_NAVY} ` +
    DISABLED_NEUTRAL,
  /* Tonal blue chip. Hover fills to navy. */
  tertiary:
    'bg-blue-5 text-blue-60 border border-blue-10 ' +
    `${HOVER_NAVY} ` +
    DISABLED_NEUTRAL,
  /* Same as primary but used for brand-level CTAs (could carry a stronger
     emphasis in future via shadow). Tracks primary's fill, otherwise the two
     sit side by side on the same page in visibly different navies. */
  brand:
    'bg-neutral-70 text-constant-white border border-neutral-70 ' +
    `${HOVER_NAVY} ` +
    'disabled:bg-neutral-5 disabled:text-text-disabled disabled:border-neutral-10',
  /* Transparent text-style with full navy fill on hover. */
  ghost:
    'bg-transparent text-blue-100 border border-transparent ' +
    `${HOVER_NAVY} ` +
    'disabled:bg-transparent disabled:text-neutral-15 disabled:border-transparent',
  /* Destructive action, white card with a thin red border + red text.
     Hover INVERTS to a filled red, mirroring how `secondary` fills navy. */
  danger:
    'bg-constant-white text-red-60 border border-red-20 ' +
    `${HOVER_RED} ` +
    DISABLED_NEUTRAL,
  /* Glass, translucent white fill for use on dark / image backgrounds.
     Rest sits at ~12% white (#FFFFFF1F); hover INVERTS to a solid white
     card with navy text so the state change reads clearly on any backdrop. */
  glass:
    'bg-[#FFFFFF1F] text-constant-white border border-[#FFFFFF2E] ' +
    'hover:bg-constant-white hover:text-blue-100 hover:border-constant-white ' +
    'focus-visible:bg-constant-white focus-visible:text-blue-100 focus-visible:border-constant-white ' +
    'active:bg-constant-white active:text-blue-100 active:border-constant-white ' +
    'disabled:bg-[#FFFFFF1F] disabled:text-constant-white/50 disabled:border-[#FFFFFF2E]',
};

const semanticStyles: Record<ButtonAppearance, Record<ButtonColor, string>> = {
  /* Solid, already filled at rest. Primary lightens; secondary (green)
     lightens; white inverts to navy on hover. */
  solid: {
    primary:
      'bg-neutral-70 text-constant-white border border-neutral-70 ' +
      `${HOVER_NAVY} ` +
      'disabled:bg-neutral-10 disabled:text-neutral-15 disabled:border-neutral-10',
    secondary:
      'bg-green-60 text-constant-white border border-green-60 ' +
      `${HOVER_GREEN_LIGHTEN} ` +
      'disabled:bg-neutral-10 disabled:text-neutral-15 disabled:border-neutral-10',
    white:
      'bg-constant-white text-blue-60 border border-blue-10 ' +
      `${HOVER_NAVY} ` +
      DISABLED_NEUTRAL,
  },
  /* Bordered, outlined at rest, fills to brand color on hover. */
  bordered: {
    primary:
      'bg-constant-white text-blue-60 border border-blue-60 ' +
      `${HOVER_NAVY} ` +
      DISABLED_NEUTRAL,
    secondary:
      'bg-constant-white text-green-60 border border-green-60 ' +
      `${HOVER_GREEN} ` +
      DISABLED_NEUTRAL,
    white:
      'bg-constant-white text-neutral-70 border border-neutral-10 ' +
      `${HOVER_NAVY} ` +
      DISABLED_NEUTRAL,
  },
  /* Text, inline link style. Subtle tonal background appears on hover
     so the link feels interactive without being heavy. */
  text: {
    primary:
      'bg-transparent text-blue-60 border-0 ' +
      'hover:bg-blue-5 hover:text-blue-100 ' +
      'focus-visible:bg-blue-5 focus-visible:text-blue-100 ' +
      'active:bg-blue-10 active:text-blue-100 ' +
      'disabled:bg-transparent disabled:text-neutral-15',
    secondary:
      'bg-transparent text-green-60 border-0 ' +
      'hover:bg-green-5 hover:text-green-60 ' +
      'focus-visible:bg-green-5 focus-visible:text-green-60 ' +
      'active:bg-green-10 active:text-green-60 ' +
      'disabled:bg-transparent disabled:text-neutral-15',
    white:
      'bg-transparent text-neutral-70 border-0 ' +
      'hover:bg-blue-5 hover:text-blue-100 ' +
      'focus-visible:bg-blue-5 focus-visible:text-blue-100 ' +
      'active:bg-blue-10 active:text-blue-100 ' +
      'disabled:bg-transparent disabled:text-neutral-15',
  },
  /* Icon, bordered tile in the brand palette. Rest carries a thin border
     and the brand icon color; hover INVERTS to a filled navy tile so the
     state change is unmistakable. Used for header utility buttons
     (cart / favorites / user / notifications / menu / search) and
     pagination prev/next chevrons, every icon-only button on the site
     should look + behave the same way. */
  icon: {
    primary:
      'bg-blue-5 text-blue-100 border border-blue-10 ' +
      `${HOVER_NAVY} ` +
      DISABLED_NEUTRAL,
    secondary:
      'bg-green-5 text-green-60 border border-green-15 ' +
      `${HOVER_GREEN} ` +
      DISABLED_NEUTRAL,
    white:
      'bg-constant-white text-blue-100 border border-neutral-10 ' +
      `${HOVER_NAVY} ` +
      DISABLED_NEUTRAL,
  },
};

function resolveTokens(variant: ButtonVariant, color: ButtonColor, appearance?: ButtonAppearance): string {
  if (
    variant === 'primary' ||
    variant === 'secondary' ||
    variant === 'tertiary' ||
    variant === 'brand' ||
    variant === 'ghost' ||
    variant === 'danger' ||
    variant === 'glass'
  ) {
    return legacyStyles[variant];
  }
  return semanticStyles[appearance ?? variant][color];
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = 'primary',
  color = 'primary',
  appearance,
  size = 'md',
  asChild = false,
  startIcon,
  endIcon,
  raw = false,
  className = '',
  children,
  type = 'button',
  ...props
}: ButtonProps, ref) {
  const classes = raw
    ? join(rawBase, className)
    : join(base, sizeStyles[size], resolveTokens(variant, color, appearance), className);
  const isIconSize = size === 'icon-sm' || size === 'icon-md';

  const content = (label: React.ReactNode) => {
    if (raw) {
      return label;
    }
    if (isIconSize) {
      return <span className="flex items-center justify-center">{label}</span>;
    }
    // Mixed labels ("Text <Icon/>", common with asChild links) can't live in an
    // inline `truncate` span: Tailwind preflight makes <svg> display:block, which
    // forces the icon onto its own line there. Give element labels a nowrap flex
    // row instead; plain text keeps the ellipsis-truncating span.
    const hasElementLabel = React.Children.toArray(label).some((c) => React.isValidElement(c));
    return (
      <span className="inline-flex min-w-0 items-center gap-1.5">
        {startIcon ? <span className="flex h-4 w-4 shrink-0 items-center justify-center">{startIcon}</span> : null}
        {hasElementLabel ? (
          <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap [&_svg]:shrink-0">{label}</span>
        ) : (
          <span className="truncate">{label}</span>
        )}
        {endIcon ? <span className="flex h-4 w-4 shrink-0 items-center justify-center">{endIcon}</span> : null}
      </span>
    );
  };

  if (asChild) {
    const child = React.Children.only(children);
    if (!React.isValidElement(child)) return null;
    const childProps = child.props as Record<string, unknown> & {
      className?: string;
      children?: React.ReactNode;
    };
    // Forward aria-*/role to the rendered element so a11y attributes set on
    // <Button asChild>...<Link>...</Link></Button> reach the resulting <a>.
    // The child's own aria-* wins if both are provided.
    const ariaPassthrough: Record<string, unknown> = {};
    for (const key of Object.keys(props)) {
      if (key.startsWith('aria-') || key === 'role') {
        if (childProps[key] === undefined) ariaPassthrough[key] = (props as Record<string, unknown>)[key];
      }
    }
    return React.cloneElement(
      child as React.ReactElement<{ className?: string; ref?: React.Ref<HTMLButtonElement> }>,
      { ...ariaPassthrough, className: join(classes, childProps.className), ref },
      <>{content(childProps.children)}</>
    );
  }

  return (
    <button ref={ref} type={type} className={classes} {...props}>
      {content(children)}
    </button>
  );
});

export default Button;
